/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { clone, uniqBy } from 'lodash'
import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { DateComponent } from '../DateComponent'
import { ContactCell } from '../shared/ContactCell'
import { ItemsTitle } from '../shared/styled/ItemsTitle'
import { AnchorCell } from '../table/AnchorCell'
import SimpleTable from '../table/SimpleTable'
import TagsCell from '../tags/TagsCell'
import { ContactDrawer } from './ContactDrawer'
import { useListTraversal } from 'app/hooks/useListTraversal'
import type { ContactJson } from 'app/types/entities'
import { notifyError } from 'app/services/notify.client'
import { SearchResponse } from 'app/services/search'
import { useContactStore } from 'app/stores/StoreContext'
import { MARKETING_CONTACTS_MODAL_QUERY } from 'app/graphql/marketingContacts'
import { gqlQuery } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { currentUrlWithParam } from 'lib/currentUrlWithParam'
import { getLeadStatusColorMap } from 'lib/colorMappings'

export type ModalType = `New Patients` | `Scheduled New Patients` | `Reappointment Rate`

export const contactsColumns = (
  contactHandler: (contactId: string) => void,
  onTagsChange: (contactId: string, tags: string[]) => void,
  tagOverridesRef?: React.MutableRefObject<Map<string, string[]>>,
): ColumnDef<ContactJson, any>[] => [
  {
    accessorFn: (row) => row.name,
    id: `Name`,
    header: `Name`,
    enableHiding: false,
    meta: {
      sortKey: `name.plain`,
      defaultVisible: true,
      pinned: true,
      ControlledComponent: (row) => (
        <AnchorCell
          href={currentUrlWithParam(`drawer`, row?.id!)}
          tooltipMessage={row?.name ?? ``}
          clickHandler={() => contactHandler(row?.id!)}
        >
          <ContactCell contact={row as ContactJson} />
        </AnchorCell>
      ),
    },
  },
  {
    accessorFn: (row) => row.tags,
    header: `Tags`,
    id: `Tags`,
    enableSorting: false,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row) => (
        <TagsCell
          tags={tagOverridesRef?.current.get(row?.id!) ?? row?.tags}
          workspaceId={row?.workspaceId}
          model="contacts"
          modelId={row?.id!}
          onChange={(tags) => onTagsChange(row?.id!, tags)}
        />
      ),
    },
  },
  {
    accessorFn: (row) => row.leadStatus,
    header: `Lead Status`,
    id: `Lead Status`,
    cell: (props) => (
      <>
        <span
          className="mr-1.5 inline-block h-2 w-2 rounded-[2px] align-middle"
          style={{ backgroundColor: getLeadStatusColorMap(props.row.original.leadStatus) }}
        ></span>
        <span>{props.row.original.leadStatus}</span>
      </>
    ),
    meta: {
      sortKey: `leadStatus.keyword`,
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.lifecycleStage,
    header: `Lifecycle Stage`,
    id: `Lifecycle Stage`,
    meta: {
      sortKey: `lifecycleStage.keyword`,
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.firstActivityType,
    header: `First Activity Type`,
    id: `First Activity Type`,
    meta: {
      sortKey: `firstActivityType.keyword`,
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.firstContactReason,
    header: `First Contact Reason`,
    id: `First Contact Reason`,
    meta: {
      sortKey: `firstContactReason.keyword`,
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.becameLeadDate,
    header: `Became Lead Date`,
    id: `Became Lead Date`,
    meta: {
      sortKey: `becameLeadDate`,
      defaultVisible: true,
    },
    cell: (props) => <DateComponent value={props.getValue()} />,
  },
  {
    accessorFn: (row) => row.firstAppointmentMadeDate,
    header: `Became a New Patient Scheduled Date`,
    id: `Became a New Patient Scheduled Date`,
    meta: {
      sortKey: `firstAppointmentMadeDate`,
      defaultVisible: false,
    },
    cell: (props) => <DateComponent value={props.getValue()} dateOnly />,
  },
  {
    accessorFn: (row) => row.becamePatientDate,
    header: `Became a Patient Date`,
    id: `Became a Patient Date`,
    meta: {
      sortKey: `becamePatientDate`,
      defaultVisible: true,
    },
    cell: (props) => <DateComponent value={props.getValue()} dateOnly />,
  },
]

export function NewPatientsModal({
  fromDate,
  toDate,
  locationId,
  filterBy,
  title,
  subtitle,
  onClose,
}: {
  fromDate?: string
  toDate?: string
  locationId: string
  filterBy: ModalType
  title: string
  subtitle?: string
  onClose: () => void
}) {
  const [patientRows, setPatientRows] = useState<ContactJson[]>()
  const [haveMore, setHaveMore] = useState(true)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [focusedContact, setFocusedContact] = useState<ContactJson>()
  const [openDrawer, setOpenDrawer] = useState(false)
  const [searchParams] = useSearchParams()
  const channel = useMemo(() => searchParams.get(`channel`), [searchParams])
  const [listLoading, setListLoading] = useState(false)
  const contactStore = useContactStore()

  const tagOverridesRef = useRef<Map<string, string[]>>(new Map())

  const applyTagOverrides = (contacts: ContactJson[]): ContactJson[] => {
    if (!tagOverridesRef.current.size) return contacts
    return contacts.map((contact) => {
      const overrideTags = tagOverridesRef.current.get(contact.id!)
      return overrideTags !== undefined ? { ...contact, tags: overrideTags } : contact
    })
  }

  const scrollHandler: React.UIEventHandler<HTMLDivElement> = (event: any) => {
    let element = event.target
    if (haveMore && !listLoading && Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 800) {
      setPage((p) => p + 1)
    }
  }

  const closeDrawerHandler = () => {
    setFocusedContact(undefined)
    setOpenDrawer(false)
    let url = new URL(document.location.href)
    url.searchParams.delete(`drawer`)
    history.replaceState({}, ``, url.toString())
  }

  function loadContact() {
    let url = new URL(document.location.href)
    setFocusedContact(undefined)
    let contactId = url.searchParams.get(`drawer`) || ``
    if (!contactId) {
      setOpenDrawer(false)
      return
    }
    bg(async () => {
      const result = await contactStore.fetchOne(contactId)
      const response = (result.data as any)?.contact as (ContactJson | undefined) & { error?: string }
      if (response?.id) {
        setFocusedContact(response)
      } else {
        notifyError(`Contact not found`)
        url.searchParams.delete(`drawer`)
        history.replaceState({}, ``, url.toString())
        setOpenDrawer(false)
        return
      }
    })
  }

  const contactHandler = (contactId: string) => {
    if (focusedContact?.id === contactId) return
    bg(async () => {
      let url = new URL(document.location.href)
      url.searchParams.set(`drawer`, contactId)
      history.replaceState(``, ``, url.toString())
      setFocusedContact(undefined)
      setOpenDrawer(true)
      const result = await contactStore.fetchOne(contactId)
      const data = (result.data as any)?.contact as (ContactJson | undefined) & { error?: string }
      if (!data || result.error) {
        notifyError((result.error as any)?.message ?? `Contact not found`)
        setFocusedContact(undefined)
        setOpenDrawer(false)
      } else {
        setFocusedContact(data as ContactJson)
        setOpenDrawer(true)
      }
    })
  }

  useEffect(() => {
    if (haveMore && !listLoading) {
      bg(async () => {
        setListLoading(true)
        try {
          const result = await gqlQuery(MARKETING_CONTACTS_MODAL_QUERY, {
            filterBy,
            fromDate: fromDate || `2000-01-01`,
            toDate: toDate || new Date().toISOString().slice(0, 10),
            locationIds: [locationId],
            pagination: { page, limit: 25 },
            channel: channel || undefined,
          })
          const hits = result.data?.marketingContacts?.hits
          const total = result.data?.marketingContacts?.total

          if (!hits?.length) {
            setHaveMore(false)
          } else {
            setHaveMore(true)
          }
          setTotal(total || 0)
          setPatientRows((items) => {
            if (page === 0) {
              return applyTagOverrides((hits || []) as ContactJson[])
            } else {
              return clone(uniqBy((items || []).concat(applyTagOverrides((hits || []) as ContactJson[])), `id`))
            }
          })
        } finally {
          setListLoading(false)
        }
      })
    }
  }, [haveMore, page])

  const contactTraversal = useListTraversal<ContactJson>(patientRows ?? [], focusedContact, (item) => {
    if (item.id) contactHandler(item.id)
  })

  const onTagsChange = useCallback((contactId: string, tags: string[]) => {
    tagOverridesRef.current.set(contactId, tags)
    setPatientRows((items) => {
      if (!items?.length) return items
      return items.map((item) => (item.id === contactId ? { ...item, tags } : item))
    })
  }, [])

  const columns = useMemo(
    () => contactsColumns(contactHandler, onTagsChange, tagOverridesRef),
    [contactHandler, onTagsChange],
  )

  return (
    <div x-component="RevenueDetails" className="flex h-[100vh] max-h-[100vh] flex-col overflow-hidden px-5">
      <div className="flex h-full w-full">
        <div className="flex h-full w-full flex-col overflow-hidden">
          <ItemsTitle title={title} subtitle={subtitle} items={total} onClose={onClose} />
          <SimpleTable
            data={patientRows}
            columns={columns}
            showColumnOrdering={true}
            onScroll={scrollHandler}
            hideFooter={true}
          />
        </div>
        <ContactDrawer
          openDrawer={openDrawer}
          contact={focusedContact}
          onClose={closeDrawerHandler}
          onChange={loadContact}
          nextContact={contactTraversal.next}
          previousContact={contactTraversal.prev}
          isNavigating={contactTraversal.isNavigating}
        />
      </div>
    </div>
  )
}
